import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createSignupCheckout } from '../../lib/signup/signupApi';
import AuthLayout from '../../components/auth/AuthLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ALL_TRADES, TRADE_META } from '../../utils/constants';

const {
  FiArrowLeft,
  FiArrowRight,
  FiAlertCircle,
  FiBriefcase,
  FiUser,
  FiMail,
  FiPhone,
  FiCheck,
  FiCreditCard,
  FiZap,
  FiBuilding,
  FiShield
} = FiIcons;

const TOTAL_STEPS = 4;

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Plans mirror the Stripe test-mode prices (docs/signup.md §5 + schema
// `plan` select: free|starter|pro|enterprise). Pricing decision recorded in
// signup.md implementation notes: Starter £19 / Pro £49 / Enterprise £99.
export const SIGNUP_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '£19',
    per: '/month',
    tagline: 'Small teams getting compliant',
    icon: FiZap,
    features: ['3 team seats', 'Unlimited certificates', 'Email support']
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '£49',
    per: '/month',
    tagline: 'Growing multi-trade businesses',
    icon: FiBuilding,
    features: ['10 team seats', 'Custom branding', 'Priority support'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '£99',
    per: '/month',
    tagline: 'Multi-branch operations',
    icon: FiShield,
    features: ['50 team seats', 'Custom contracts', 'SSO integration']
  }
];

const STEPS = [
  { number: 1, label: 'Company' },
  { number: 2, label: 'Trades' },
  { number: 3, label: 'Plan' },
  { number: 4, label: 'Pay' }
];

/**
 * Organization registration wizard (docs/signup.md §3).
 * The frontend NEVER creates organizations/users records (that was the 403
 * bug — createRule: null is correct). Flow:
 *   1. Company + contact details
 *   2. Trade sector selection
 *   3. Plan choice (Stripe subscription tiers)
 *   4. Terms → POST /api/signup/checkout → redirect to Stripe Checkout URL
 * Account + OTP email happen server-side after payment confirmation
 * (webhook); /signup/success polls for the account then runs OTP login.
 */
const Signup = () => {
  const { authError, clearAuthError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Step 1 fields
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2 fields
  const [selectedTrades, setSelectedTrades] = useState([]);

  // Step 3 fields
  const [selectedPlan, setSelectedPlan] = useState('pro');

  // Step 4 fields
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const [step, setStep] = useState(1);

  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const toggleTrade = (trade) => {
    setSelectedTrades((prev) =>
      prev.includes(trade) ? prev.filter((t) => t !== trade) : [...prev, trade]
    );
  };

  const validateStep1 = () => {
    if (!companyName.trim()) return 'Please enter your organization name.';
    if (!contactName.trim()) return 'Please enter the primary contact name.';
    if (!isValidEmail(email.trim())) return 'Please enter a valid email address.';
    return null;
  };

  const validateStep2 = () => {
    if (selectedTrades.length === 0) {
      return 'Select at least one trade sector — this controls which certificates your team can issue.';
    }
    return null;
  };

  const goNext = () => {
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  /**
   * Terms accepted → single call to the signup service. No PB writes here.
   * On success we redirect the whole window to Stripe's hosted checkout.
   */
  const handleProceedToCheckout = async () => {
    if (!termsAccepted) {
      setLocalError('You must accept the Terms of Service to continue.');
      return;
    }
    setLocalError(null);
    clearAuthError();
    setSubmitting(true);
    try {
      const { url: checkoutUrl } = await createSignupCheckout({
        companyName: companyName.trim(),
        contactName: contactName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        plan: selectedPlan,
        trades: selectedTrades
      });
      window.location.assign(checkoutUrl);
    } catch (err) {
      setLocalError(err.message || 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  const errorBanner = localError || authError;

  const inputClass =
    'w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';

  return (
    <AuthLayout
      title="Create your organization workspace"
      subtitle={`Step ${step} of ${TOTAL_STEPS} — multi-trade compliance from day one.`}
      footer={
        <span>
          Already registered?{' '}
          <Link to="/login" className="font-bold text-blue-600 dark:text-blue-400 hover:underline">
            Sign in with email
          </Link>
        </span>
      }
    >
      {/* Progress stepper */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.number}>
            <div className="flex items-center gap-2">
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black transition-colors ${
                  step > s.number
                    ? 'bg-emerald-500 text-white'
                    : step >= s.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                }`}
              >
                {step > s.number ? <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> : s.number}
              </span>
              <span
                className={`text-[11px] font-bold ${
                  step >= s.number ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 rounded ${step > s.number ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-800'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {checkoutCancelled && step === 1 && !errorBanner && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-start gap-2.5">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Checkout was cancelled — no payment was taken and no account was created. You can restart below.
          </p>
        </div>
      )}

      {errorBanner && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">{errorBanner}</p>
        </div>
      )}

      {/* STEP 1: Company details */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">Organization name</label>
            <div className="relative">
              <SafeIcon icon={FiBriefcase} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Acme Heating & Gas Ltd"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={`${inputClass} pl-10`}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">Primary contact name</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Sarah Mitchell"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className={`${inputClass} pl-10`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">Work email</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="email"
                  placeholder="you@company.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="appearance-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="tel"
                  placeholder="020 7946 0912"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`${inputClass} pl-10`}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={goNext}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
          >
            Continue to Trades
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* STEP 2: Trade sector selection */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Select the sectors your organization is accredited to work in. Your team will only see
            statutory certificates for these trades — you can change this later in Settings.
          </p>

          <div className="space-y-2.5">
            {ALL_TRADES.map((trade) => {
              const meta = TRADE_META[trade];
              const checked = selectedTrades.includes(trade);
              return (
                <button
                  type="button"
                  key={trade}
                  onClick={() => toggleTrade(trade)}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                    checked
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className="text-2xl leading-none mt-0.5">{meta.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{meta.label}</span>
                      {meta.accreditations.length > 0 && (
                        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                          ({meta.accreditations.join(' / ')})
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{meta.description}</span>
                  </span>
                  <span
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300 dark:border-slate-600'
                    }`}
                  >
                    {checked && <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setLocalError(null);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
            >
              Continue to Plan
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Plan selection */}
      {step === 3 && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Choose your subscription plan. You'll pay securely via Stripe after reviewing your details.
          </p>

          <div className="space-y-2.5">
            {SIGNUP_PLANS.map((plan) => {
              const selected = selectedPlan === plan.id;
              const PlanIcon = plan.icon;
              return (
                <button
                  type="button"
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                    selected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                    <SafeIcon icon={PlanIcon} className="w-4.5 h-4.5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-900 dark:text-slate-100">{plan.name}</span>
                      {plan.popular && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">
                          Popular
                        </span>
                      )}
                      <span className="ml-auto text-sm font-black text-slate-900 dark:text-slate-100">
                        {plan.price}
                        <span className="text-[10px] font-semibold text-slate-400">{plan.per}</span>
                      </span>
                    </span>
                    <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{plan.tagline}</span>
                    <span className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                      {plan.features.map((f) => (
                        <span key={f} className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-emerald-500" />
                          {f}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => {
                setStep(2);
                setLocalError(null);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all"
            >
              Continue to Payment
              <SafeIcon icon={FiCreditCard} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: Review + terms → Stripe Checkout */}
      {step === 4 && (
        <div className="space-y-5">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div><span className="text-slate-500">Organization:</span> <span className="font-bold text-slate-800 dark:text-slate-200">{companyName}</span></div>
              <div><span className="text-slate-500">Contact:</span> <span className="font-bold text-slate-800 dark:text-slate-200">{contactName}</span></div>
              <div><span className="text-slate-500">Email:</span> <span className="font-bold text-slate-800 dark:text-slate-200 truncate">{email}</span></div>
              <div><span className="text-slate-500">Trades:</span> <span className="font-bold text-slate-800 dark:text-slate-200">{selectedTrades.length} selected</span></div>
              <div><span className="text-slate-500">Plan:</span> <span className="font-bold text-slate-800 dark:text-slate-200">{SIGNUP_PLANS.find((p) => p.id === selectedPlan)?.name} — {SIGNUP_PLANS.find((p) => p.id === selectedPlan)?.price}/mo</span></div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {selectedTrades.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">
                  {TRADE_META[t]?.icon} {TRADE_META[t]?.label || t}
                </span>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
              I accept the <span className="font-bold text-blue-600 dark:text-blue-400">Terms of Service</span> and{' '}
              <span className="font-bold text-blue-600 dark:text-blue-400">Privacy Policy</span>, and confirm my
              organization holds valid accreditation for the selected trades.
            </span>
          </label>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => {
                setStep(3);
                setLocalError(null);
              }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={handleProceedToCheckout}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold shadow-md shadow-blue-900/20 transition-all disabled:opacity-50"
            >
              {submitting ? 'Starting checkout...' : `Continue to Payment — ${SIGNUP_PLANS.find((p) => p.id === selectedPlan)?.price}/mo`}
              {!submitting && <SafeIcon icon={FiCreditCard} className="w-4 h-4" />}
            </button>
          </div>

          <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center leading-relaxed">
            You'll be redirected to Stripe's secure checkout. Your workspace is created the moment
            payment is confirmed — then we'll email you a one-time code to sign in.
          </p>
        </div>
      )}
    </AuthLayout>
  );
};

export default Signup;
